import { getApiData, sendApiData } from "./client";
import type { DictItem, ModelQueryParams, PaginationResult } from "./types";

export type LibraryItem = {
  id: string;
  name: string;
  type: string;
  sortNo?: number | null;
  enabled: boolean;
  coverUrl?: string | null;
  fileUrl?: string | null;
  fontFamily?: string;
  fileName?: string | null;
  mappingAreaX?: number | null;
  mappingAreaY?: number | null;
  fileSize?: number | null;
  fileScale?: number | null;
  description?: string | null;
  remark?: string | null;
  summary?: string | null;
  tags?: string[];
  placement?: string | null;
  role?: string | null;
  acceptsMaterial?: boolean;
  mediaKind?: string | null;
  updatedAt?: string;
};

export type AssetUsage = {
  fileCount: number;
  usedBytes: number;
  quotaBytes: number;
  remainingBytes: number;
};

export type MaterialCarpaint = {
  sourcePath?: string;
  flakes?: { path?: string; name?: string; url?: string } | null;
  chroma?: { path?: string; name?: string; url?: string } | null;
  meta?: {
    width?: number;
    height?: number;
    layers?: number;
    scale?: number;
    numThetaF?: number;
    numThetaI?: number;
    patchLookupIndices?: number[];
    sizeMM?: number | null;
    clearcoatIor?: number | null;
    clearcoat?: number | null;
  };
};

export type MaterialRenderParams = {
  roughness?: number;
  metalness?: number;
  opacity?: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  transmission?: number;
  ior?: number;
  thickness?: number;
  color?: string;
  emissive?: string;
  emissiveIntensity?: number;
  sheen?: number;
  sheenRoughness?: number;
  sheenColor?: string;
  specularIntensity?: number;
  envMapIntensity?: number;
  iridescence?: number;
  iridescenceIOR?: number;
  environmentIntensity?: number;
  toneMappingExposure?: number;
  /** 1 为原始密度，越大纹理和闪片越密 */
  uvRepeat?: number;
  surfaceAntiRepeat?: number;
  surfaceSeed?: number;
  flakeGain?: number;
  flakeTile?: number;
  flakeMean?: number;
  flakeBase?: number;
  uniforms?: Record<string, { type: "number" | "color" | "boolean"; value: number | string | boolean }>;
};

export type MaterialShowcase = {
  displayName?: string | null;
  substrate?: string | null;
  colorHex?: string | null;
  weatherResistance?: number | string | null;
  scratchResistance?: string | null;
  labL?: number | null;
  labA?: number | null;
  labB?: number | null;
  glossiness?: number | null;
  tags?: string[];
  rgbaColors?: string[];
};

export type MaterialCategoryOption = {
  id: string | number;
  label: string;
  value: string;
  parentId?: string | null;
};

export type MaterialItem = LibraryItem & {
  tagLabels?: string[];
  kind?: string;
  code?: string | null;
  categoryId?: string | null;
  fileFormat?: string;
  renderParams?: MaterialRenderParams | null;
  /** 720px webp 封面；后端未部署该字段时为空。 */
  coverThumbUrl?: string | null;
  /** 256px webp 基础色贴图，用于首页等轻量 3D 预览。 */
  mapThumbUrl?: string | null;
  carpaint?: MaterialCarpaint | null;
  showcase?: MaterialShowcase | null;
  summary?: string | null;
  colorStory?: string | null;
  tags?: string[];
  finish?: string | null;
  colorFamily?: string | null;
  audience?: string;
  locked?: boolean;
  requiredPlan?: string | null;
  requiredPlanName?: string | null;
};

export type MoodboardScene = {
  models: Array<LibraryItem & { fileScale?: number }>;
  materials: MaterialItem[];
  accessories: LibraryItem[];
  controls: Array<{
    key: 'roughness' | 'metalness' | 'environmentIntensity';
    label: string;
    min: number;
    max: number;
    step: number;
    value: number;
  }>;
  generatedAt: string;
};

export const libraryApi = {
  getAccessories: (params?: ModelQueryParams) =>
    getApiData<PaginationResult<LibraryItem>>("/client/accessories", { params }),
  getMyAccessories: (params?: ModelQueryParams) =>
    getApiData<PaginationResult<LibraryItem>>("/client/accessories/mine", { params }),
  getMyAssetUsage: () => getApiData<AssetUsage>("/client/accessories/usage"),
  createAccessory: (payload: { name: string; type?: string; file: File; cover?: File }) => {
    const data = new FormData();
    data.append("name", payload.name);
    if (payload.type) data.append("type", payload.type);
    data.append("file", payload.file);
    if (payload.cover) data.append("cover", payload.cover);
    return sendApiData<LibraryItem>("post", "/client/accessories", data, { timeout: 180000 });
  },
  removeAccessory: (id: string) => sendApiData<void>("delete", `/client/accessories/${id}`),
  getAccessory: (id: string) => getApiData<LibraryItem>(`/client/accessories/${id}`),
  getAccessoryTypes: () => getApiData<DictItem[]>("/client/accessories/types"),
  getTextures: (params?: ModelQueryParams) =>
    getApiData<PaginationResult<LibraryItem>>("/client/textures", { params }),
  getTextureTypes: () => getApiData<DictItem[]>("/client/textures/types"),
  getFonts: (params?: ModelQueryParams) =>
    getApiData<PaginationResult<LibraryItem>>("/client/fonts", { params }),
  getFont: (id: string) => getApiData<LibraryItem>(`/client/fonts/${id}`),
  getFontTypes: () => getApiData<DictItem[]>("/client/fonts/types"),
  getMaterials: (params?: ModelQueryParams & { kind?: string; categoryId?: string }) =>
    getApiData<PaginationResult<MaterialItem>>("/client/materials", { params }),
  getMaterial: (id: string) => getApiData<MaterialItem>(`/client/materials/${id}`),
  getMaterialTypes: (kind?: string) =>
    getApiData<MaterialCategoryOption[]>("/client/materials/types", { params: kind ? { kind } : undefined }),
  getMoodboardScene: (params?: { modelLimit?: number; accessoryLimit?: number }) =>
    getApiData<MoodboardScene>("/client/moodboard/scene", { params }),
};
